/**
 * Per-surface toolbar presets — WHICH items, in WHAT order, with WHAT
 * collapse priority, and which live only in the ⋯ More menu.
 *
 * Source of truth is the toolbar UX spec (plan `MOTE-20-wysiwyg-toolbar-overflow.md`,
 * `## UX spec (final)` §B — a plan-file reference, not a code ticket tag). Priorities: LOWER collapses first; ties collapse the rightmost item
 * first. `moreOnly` items never render in the row but always appear in the
 * menu — a preset with any of them shows ⋯ permanently.
 *
 * Dividers are not listed: the row draws one between adjacent VISIBLE items
 * whose `group` differs (see `EditorToolbar`), so they survive any hide
 * order without bookkeeping here.
 */

import type { EditorSurface } from '../extensions'
import type { ToolbarItemId } from './toolbar-items'

export type ToolbarPresetId = 'card' | 'comment' | 'wiki' | 'wiki-comment' | 'chat'

export interface ToolbarPresetItem {
  id: ToolbarItemId
  priority: number
}

export interface ToolbarPreset {
  id: ToolbarPresetId
  /** Row order. */
  items: readonly ToolbarPresetItem[]
  /** Menu-only items, in menu order. */
  moreOnly: readonly ToolbarItemId[]
  /** Button treatment. `subtle` = the Slack-style idle/pressed used by chat + bare clusters. */
  tone: 'default' | 'subtle'
  /** Button size. `sm` = the bare wiki-comment cluster's 28px host rhythm. */
  size: 'md' | 'sm'
  /** Flex gap between row children, in px — must match the row's Tailwind gap class. */
  gap: number
  /** The `ToolbarItem.group` of every item + surface tier the drift test checks against. */
  surface: EditorSurface
  /**
   * Radius of the HOST box the card-chrome row sits in, so the row's muted
   * fill can take the matching INNER radius (host radius − 1px border) —
   * a mismatch shows as a square fill poking past the curve, or a sliver
   * of host background at the corner tip. `md` = card / comment composers
   * (`rounded-md`, 6px), `lg` = the chat composers (`rounded-lg`, 8px).
   * Irrelevant to flush / bare rows.
   */
  hostRadius: 'md' | 'lg'
}

const row = (...entries: Array<[ToolbarItemId, number]>): ToolbarPresetItem[] =>
  entries.map(([id, priority]) => ({ id, priority }))

export const TOOLBAR_PRESETS: Readonly<Record<ToolbarPresetId, ToolbarPreset>> = {
  // Card description (detail + create), clients notes.
  // Quote is a ROW item on every preset (never a Tt▾ option — Omkar,
  // sign-off): it overflows into ⋯ like any other button.
  card: {
    id: 'card',
    items: row(
      ['text-style', 9],
      ['bold', 10],
      ['italic', 10],
      ['underline', 8],
      ['strike', 7],
      ['inline-code', 7],
      ['bullet-list', 8],
      ['ordered-list', 8],
      ['task-list', 6],
      ['quote', 5],
      ['link', 9],
      ['code-block', 5],
      ['table', 4],
      ['attach', 9],
      ['emoji', 6],
    ),
    moreOnly: ['subscript', 'superscript'],
    tone: 'default',
    size: 'md',
    gap: 4,
    surface: 'card',
    hostRadius: 'md',
  },
  // Card comments — the narrowest full-toolbar surface (a 450px column).
  comment: {
    id: 'comment',
    items: row(
      ['bold', 10],
      ['italic', 10],
      ['underline', 8],
      ['strike', 7],
      ['link', 9],
      ['inline-code', 8],
      ['quote', 7],
      ['bullet-list', 6],
      ['ordered-list', 6],
      ['attach', 9],
      ['emoji', 8],
    ),
    moreOnly: ['text-style', 'task-list', 'code-block', 'table', 'subscript', 'superscript'],
    tone: 'default',
    size: 'md',
    gap: 4,
    // Card comments run `<MarkdownEditor>` on the `card` surface (the
    // `comment` schema set belongs to the wiki comment composers).
    surface: 'card',
    hostRadius: 'md',
  },
  // Wiki doc — external full-width flush row. The wiki insert catalog
  // (panels / expand / status / date / …) is appended by the DocPage via
  // `extraMenuEntries`; it isn't a toolbar item.
  wiki: {
    id: 'wiki',
    items: row(
      ['text-style', 9],
      ['bold', 10],
      ['italic', 10],
      ['underline', 8],
      ['strike', 7],
      ['inline-code', 7],
      ['bullet-list', 8],
      ['ordered-list', 8],
      ['task-list', 7],
      ['quote', 6],
      ['link', 9],
      ['code-block', 6],
      ['table', 6],
      ['attach', 9],
      ['emoji', 5],
    ),
    moreOnly: ['subscript', 'superscript'],
    tone: 'default',
    size: 'md',
    gap: 6,
    surface: 'wiki',
    hostRadius: 'md',
  },
  // Wiki comment composers — a BARE cluster nested in the host's action
  // row (`@ 😊 │ … │ Cancel Post`), so no wrapper, no role, 28px buttons.
  'wiki-comment': {
    id: 'wiki-comment',
    items: row(
      ['bold', 10],
      ['italic', 10],
      ['underline', 8],
      ['strike', 7],
      ['link', 9],
      ['inline-code', 8],
      ['quote', 7],
      ['attach', 9],
    ),
    moreOnly: [],
    tone: 'subtle',
    size: 'sm',
    gap: 2,
    surface: 'comment',
    hostRadius: 'md',
  },
  // Chat composer + message edit (the old `compact` variant).
  chat: {
    id: 'chat',
    items: row(
      ['bold', 10],
      ['italic', 10],
      ['underline', 8],
      ['strike', 7],
      ['link', 9],
      ['ordered-list', 6],
      ['bullet-list', 6],
      ['quote', 5],
      ['inline-code', 8],
      ['code-block', 7],
    ),
    moreOnly: [],
    tone: 'subtle',
    size: 'md',
    gap: 2,
    surface: 'chat',
    hostRadius: 'lg',
  },
}

/** The preset a surface renders when the caller didn't pick one. */
export const defaultPresetForSurface = (surface: EditorSurface): ToolbarPresetId => {
  switch (surface) {
    case 'wiki':
      return 'wiki'
    case 'chat':
      return 'chat'
    // The only `surface="comment"` editors are the wiki comment composers.
    case 'comment':
      return 'wiki-comment'
    default:
      return 'card'
  }
}
